// Transaction service để fetch lịch sử giao dịch từ blockchain explorers
import { formatUnits } from 'viem';

interface ExplorerTransaction {
  hash: string;
  from: string;
  to: string;
  value: string;
  tokenSymbol?: string;
  tokenAddress?: string;
  timeStamp: string;
  isError: string;
  confirmations: string;
  gasUsed: string;
  gasPrice: string;
  blockNumber: string;
  nonce: string;
}

interface Transaction {
  hash: string;
  from: string;
  to: string;
  value: string;
  tokenAddress?: string;
  tokenSymbol?: string;
  type: 'send' | 'receive' | 'token' | 'contract';
  status: 'pending' | 'confirmed' | 'failed';
  timestamp: number;
  chainId: number;
}

// API key pools cho các explorer phổ biến
// Được trích xuất từ code JS đã cung cấp
const EXPLORER_API_KEY_POOLS: Record<string, string[]> = {
  'etherscan.io': [
    '9Y3MTWQKDYWCJR8R94RJFAKHJN1NHD5692',
    'F92Z14GE2DTF6PBBYY1YPHPJ438PT3P2VI',
    'PYM9U2QD949KZZX23QJ4YZRX3KC3PHAI88',
    '12RU83G1ATVA9V4EMM3U45X8BG4RG9PM6T',
    'TDMPDZU8RD4V9FVB66P5S47QETEJ6R61UY',
    'YIDAXPUWHJB21RJVMS1JMXHABMEF67RQWG',
    'EX8K12JY7BCVG8RAUU8X2Z6QT2GCF5EYB4',
    '36XRXZWIFCV12GKAXHCNWRI7BA325B1MZQ',
    '67PVAM591X99PTGGSMR56ET46DX7T7BR6F',
    'VQYJT8XSC3A7PXFG1VTWS82V65C6GFVKNF',
    '1U19WRCQCNJ3D8XM8MBITI1YB1ZWH4SRPG',
  ],
  'bscscan.com': [
    'PI9PC3NFCA8IT2Y4B5ZC23VEBF6KN6QWWE',
    '6PA53I5AF8D8TDM6S96G3D866GAGPREVQT',
    'FZUGKRRNW659SAAF8ZVZUMIFZ9SJMB355V',
    'U3QYZUWB4YC5U1DRAAXDUMG4SXKVS2ASUN',
  ],
  'polygonscan.com': [
    'RPPXW5VU4XU9IZ9QUGAQ8T6M5X3W73PNCJ',
    'HKW23649E839B56YAKFRIHCBWB2AZG3AVE',
    '5M88WT4JRUS587H2TTNCQ1M355UPY19M26',
  ],
  'arbiscan.io': [
    'YPFNTZWMC7YNSWZP97PSZJ25NE31MG5HS3',
  ],
  'optimistic.etherscan.io': [],
  'ftmscan.com': [
    'V8DGVKR4D9URFKQWTRNQYSQW6C1F6QXHTF',
    'GN6K97P9AC8RCDM3KDMZMKC98JV6E5ZZVM',
    '1VPMYX4CMUVU8MD8IPQN9UGDMFZQFXRRP6',
    'XP65W7M1VS93F1RXZSAHXEXJQDDX6QHX1W',
    'ES7SPC2PBVBK85Y7VRDIWGKJKQYR3A1FWK'
  ],
  'snowscan.xyz': [
    'BT8P6GC2QDK6DQ6HC8K6YQE6HBKDDTH4N2'
  ],
  'basescan.org': [],
  'celoscan.io': [],
  'gnosisscan.io': [],
  'moonscan.io': [
    'U4DYMM5RIU6U25GKM7BI9HDPKQEK75TUZC',
  ]
};

class TransactionService {
  // Lấy random API key từ pool
  private getRandomApiKey(explorerUrl: string): string {
    try {
      const url = new URL(explorerUrl);
      const hostname = url.hostname;
      
      for (const [domain, apiKeys] of Object.entries(EXPLORER_API_KEY_POOLS)) {
        if (hostname.includes(domain)) {
          // Random select một key từ pool
          const randomIndex = Math.floor(Math.random() * apiKeys.length);
          return apiKeys[randomIndex];
        }
      }
    } catch {
      // Ignore
    }
    return '';
  }

  // Lấy API endpoint từ explorer URL
  private getApiEndpoint(explorerUrl: string): string | null {
    try {
      const url = new URL(explorerUrl);
      const hostname = url.hostname;
      
      // Etherscan-based explorers
      if (hostname.includes('etherscan.io')) {
        return 'https://api.etherscan.io/api';
      }
      if (hostname.includes('bscscan.com')) {
        return 'https://api.bscscan.com/api';
      }
      if (hostname.includes('polygonscan.com')) {
        return 'https://api.polygonscan.com/api';
      }
      if (hostname.includes('arbiscan.io')) {
        return 'https://api.arbiscan.io/api';
      }
      if (hostname.includes('optimistic.etherscan.io')) {
        return 'https://api-optimistic.etherscan.io/api';
      }
      if (hostname.includes('ftmscan.com')) {
        return 'https://api.ftmscan.com/api';
      }
      if (hostname.includes('snowscan.xyz')) {
        return 'https://api.snowscan.xyz/api';
      }
      if (hostname.includes('basescan.org')) {
        return 'https://api.basescan.org/api';
      }
      if (hostname.includes('celoscan.io')) {
        return 'https://api.celoscan.io/api';
      }
      if (hostname.includes('gnosisscan.io')) {
        return 'https://api.gnosisscan.io/api';
      }
      if (hostname.includes('moonscan.io')) {
        return 'https://api.moonscan.io/api';
      }
      
      // BlockScout explorers (không cần API key)
      if (hostname.includes('blockscout')) {
        return `${explorerUrl}/api`;
      }
      
      return null;
    } catch {
      return null;
    }
  }

  // Fetch transactions từ explorer với retry logic
  async fetchTransactionHistory(
    address: string,
    chainId: number,
    explorerUrl?: string,
    limit: number = 20
  ): Promise<Transaction[]> {
    if (!explorerUrl) return [];
    
    const apiEndpoint = this.getApiEndpoint(explorerUrl);
    if (!apiEndpoint) return [];
    
    const apiKey = this.getRandomApiKey(explorerUrl);
    
    try {
      // Etherscan-style API
      if (apiEndpoint.includes('etherscan') || apiEndpoint.includes('scan.com') || apiEndpoint.includes('scan.org') || apiEndpoint.includes('scan.io') || apiEndpoint.includes('scan.xyz') || apiEndpoint.includes('moonscan')) {
        const params = new URLSearchParams({
          module: 'account',
          action: 'txlist',
          address: address,
          startblock: '0',
          endblock: '99999999',
          page: '1',
          offset: limit.toString(),
          sort: 'desc',
          ...(apiKey && { apikey: apiKey })
        });
        
        const response = await fetch(`${apiEndpoint}?${params}`);
        const data = await response.json();
        
        if (data.status === '1' && Array.isArray(data.result)) {
          return data.result.map((tx: ExplorerTransaction) => ({
            hash: tx.hash,
            from: tx.from,
            to: tx.to,
            value: formatUnits(BigInt(tx.value), 18),
            type: tx.from.toLowerCase() === address.toLowerCase() ? 'send' : 'receive',
            status: tx.isError === '0' ? 'confirmed' : 'failed',
            timestamp: parseInt(tx.timeStamp) * 1000,
            chainId,
            gasUsed: tx.gasUsed,
            gasPrice: tx.gasPrice,
            blockNumber: parseInt(tx.blockNumber),
            nonce: parseInt(tx.nonce)
          }));
        }
      }
      
      // BlockScout API
      if (apiEndpoint.includes('blockscout')) {
        const response = await fetch(`${apiEndpoint}/v2/addresses/${address}/transactions?filter=all&limit=${limit}`);
        const data = await response.json();
        
        if (data.items && Array.isArray(data.items)) {
          return data.items.map((tx: any) => ({
            hash: tx.hash,
            from: tx.from.hash,
            to: tx.to?.hash || '',
            value: formatUnits(BigInt(tx.value || '0'), 18),
            type: tx.from.hash.toLowerCase() === address.toLowerCase() ? 'send' : 'receive',
            status: tx.status === 'ok' ? 'confirmed' : 'failed',
            timestamp: new Date(tx.timestamp).getTime(),
            chainId,
            gasUsed: tx.gas_used,
            gasPrice: tx.gas_price,
            blockNumber: tx.block
          }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch transaction history:', error);
      
      // Nếu fail với một key, có thể thử lại với key khác
      // Nhưng để tránh vòng lặp vô tận, chỉ log error
    }
    
    return [];
  }

  // Fetch token transfers với retry logic
  async fetchTokenTransfers(
    address: string,
    chainId: number,
    explorerUrl?: string,
    limit: number = 20
  ): Promise<Transaction[]> {
    if (!explorerUrl) return [];
    
    const apiEndpoint = this.getApiEndpoint(explorerUrl);
    if (!apiEndpoint) return [];
    
    const apiKey = this.getRandomApiKey(explorerUrl);
    
    try {
      // Etherscan-style API
      if (apiEndpoint.includes('etherscan') || apiEndpoint.includes('scan.com') || apiEndpoint.includes('scan.org') || apiEndpoint.includes('scan.io') || apiEndpoint.includes('scan.xyz') || apiEndpoint.includes('moonscan')) {
        const params = new URLSearchParams({
          module: 'account',
          action: 'tokentx',
          address: address,
          startblock: '0',
          endblock: '99999999',
          page: '1',
          offset: limit.toString(),
          sort: 'desc',
          ...(apiKey && { apikey: apiKey })
        });
        
        const response = await fetch(`${apiEndpoint}?${params}`);
        const data = await response.json();
        
        if (data.status === '1' && Array.isArray(data.result)) {
          return data.result.map((tx: any) => ({
            hash: tx.hash,
            from: tx.from,
            to: tx.to,
            value: formatUnits(BigInt(tx.value), parseInt(tx.tokenDecimal)),
            tokenAddress: tx.contractAddress,
            tokenSymbol: tx.tokenSymbol,
            type: 'token',
            status: 'confirmed',
            timestamp: parseInt(tx.timeStamp) * 1000,
            chainId,
            gasUsed: tx.gasUsed,
            gasPrice: tx.gasPrice,
            blockNumber: parseInt(tx.blockNumber),
            nonce: parseInt(tx.nonce)
          }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch token transfers:', error);
    }
    
    return [];
  }

  // Merge và sort transactions
  mergeTransactions(
    localTxs: Transaction[],
    fetchedTxs: Transaction[],
    tokenTransfers: Transaction[]
  ): Transaction[] {
    // Create a map to avoid duplicates
    const txMap = new Map<string, Transaction>();
    
    // Add local transactions first (they might have pending status)
    localTxs.forEach(tx => {
      txMap.set(tx.hash, tx);
    });
    
    // Add fetched transactions (update if exists)
    [...fetchedTxs, ...tokenTransfers].forEach(tx => {
      const existing = txMap.get(tx.hash);
      if (!existing || existing.status === 'pending') {
        txMap.set(tx.hash, tx);
      }
    });
    
    // Convert back to array and sort by timestamp
    return Array.from(txMap.values())
      .sort((a, b) => b.timestamp - a.timestamp);
  }
}

export const transactionService = new TransactionService(); 